import { makeT } from "app/client/lib/localization";
import { getHomeUrl } from "app/client/models/AppModel";
import { reportError } from "app/client/models/errors";
import { HomeModel } from "app/client/models/HomeModel";
import { AssistantAPIImpl } from "app/client/ui/AssistantAPI";
import { getHomeAssistantPrompts } from "app/client/ui/AssistantPrompts";
import { buildAssistantStartBlock } from "app/client/ui/AssistantStartBlock";
import { isNarrowScreen } from "app/client/ui2018/cssVars";
import { getGristConfig } from "app/common/urlUtils";

import { Computed, dom, DomContents, IDisposableOwner, Observable } from "grainjs";

const t = makeT("HomeAssistant");

interface HomeAssistantComposerOptions {
  homeModel: HomeModel;
  showIntro: boolean;
}

/**
 * Returns true if the home page should show the assistant composer.
 */
export function hasHomeAssistantComposer(): boolean {
  return getGristConfig().assistant?.version === 2;
}

/**
 * The assistant composer on the home page.
 *
 * Creates a new document in `homeModel.newDocWorkspace` and redirects to it,
 * submitting the input as a prompt to the assistant.
 */
export function buildHomeAssistantComposer(
  owner: IDisposableOwner,
  { homeModel, showIntro }: HomeAssistantComposerOptions,
): DomContents {
  if (!hasHomeAssistantComposer()) { return null; }

  const assistantAPI = new AssistantAPIImpl(getHomeUrl());
  const isAnonymous = !homeModel.app.currentValidUser;
  const enabled = Computed.create(owner, use => isAnonymous || Boolean(use(homeModel.newDocWorkspace)));
  const pending = Observable.create(owner, false);
  const errorText = Observable.create(owner, "");

  async function submit(prompt: string) {
    const destWS = homeModel.newDocWorkspace.get();
    errorText.set("");
    pending.set(true);
    try {
      const { redirectUrl } = await assistantAPI.start({
        prompt,
        developerPromptVersion: isAnonymous ? "new-document" : "new-document-no-intro",
        workspaceId: (!isAnonymous && destWS && destWS !== "unsaved") ? destWS.id : undefined,
      });
      window.location.assign(redirectUrl);
    } catch (err) {
      pending.set(false);
      errorText.set(t("Failed to create document. Please try again later."));
      reportError(err);
    }
  }

  return dom("div",
    dom.create(buildAssistantStartBlock, {
      prompts: getHomeAssistantPrompts(),
      placeholder: t("Describe the document you want to build…"),
      showIntro,
      revealDelayMs: 160,
      enabled,
      disabledPlaceholder: t("You need edit access to create documents here."),
      autoFocus: !isNarrowScreen(),
      pending,
      errorText,
      onSubmit: (text) => { void submit(text); },
    }),
    dom.cls("test-home-assistant"),
  );
}
